(() => {
  /* ========= Dawn core helpers (patched for TT safety) ========= */

  function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  }

  const trapFocusHandlers = {};

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener("focusin", trapFocusHandlers.focusin);
    document.removeEventListener("focusout", trapFocusHandlers.focusout);
    document.removeEventListener("keydown", trapFocusHandlers.keydown);

    if (elementToFocus && elementToFocus.focus) elementToFocus.focus();
  }

  function onKeyUpEscape(event) {
    if ((event.code || "").toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector("summary");
    openDetailsElement.removeAttribute("open");
    if (summaryElement) {
      summaryElement.setAttribute("aria-expanded", false);
      summaryElement.focus();
    }
  }

  function trapFocus(container, elementToFocus = container) {
    if (!container) return;

    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    // SAFETY: nothing focusable = don’t trap
    if (!first || !last) {
      if (elementToFocus && elementToFocus.focus) elementToFocus.focus();
      return;
    }

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== last && event.target !== first) return;
      document.addEventListener("keydown", trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (event) => {
      if ((event.code || "").toUpperCase() !== "TAB") return;

      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("focusout", trapFocusHandlers.focusout);
    document.addEventListener("focusin", trapFocusHandlers.focusin);

    if (elementToFocus && elementToFocus.focus) elementToFocus.focus();

    if (
      elementToFocus &&
      elementToFocus.tagName === "INPUT" &&
      ["search", "text", "email", "url"].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }

  function pauseAllMedia() {
    document.querySelectorAll(".js-youtube").forEach((video) => {
      try {
        video.contentWindow?.postMessage?.('{"event":"command","func":"pauseVideo","args":""}', "*");
      } catch (e) {}
    });

    document.querySelectorAll(".js-vimeo").forEach((video) => {
      try {
        video.contentWindow?.postMessage?.('{"method":"pause"}', "*");
      } catch (e) {}
    });

    document.querySelectorAll("video").forEach((video) => {
      try {
        video.pause();
      } catch (e) {}
    });

    document.querySelectorAll("product-model").forEach((model) => {
      try {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      } catch (e) {}
    });
  }

  // expose once, safely
  window.pauseAllMedia = pauseAllMedia;

  /* Details summary a11y */
  document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    summary.setAttribute("role", "button");
    summary.setAttribute("aria-expanded", summary.parentNode?.hasAttribute?.("open") || false);

    // SAFETY: nextElementSibling can be null on some custom markup
    const next = summary.nextElementSibling;
    if (next && next.getAttribute && next.getAttribute("id")) {
      summary.setAttribute("aria-controls", next.id);
    }

    summary.addEventListener("click", (event) => {
      const details = event.currentTarget.closest("details");
      if (!details) return;
      event.currentTarget.setAttribute("aria-expanded", !details.hasAttribute("open"));
    });

    if (summary.closest("header-drawer, menu-drawer")) return;

    // SAFETY: parentElement may be null
    if (summary.parentElement) summary.parentElement.addEventListener("keyup", onKeyUpEscape);
  });

  try {
    document.querySelector(":focus-visible");
  } catch (e) {
    focusVisiblePolyfill();
  }

  function focusVisiblePolyfill() {
    const navKeys = [
      "ARROWUP",
      "ARROWDOWN",
      "ARROWLEFT",
      "ARROWRIGHT",
      "TAB",
      "ENTER",
      "SPACE",
      "ESCAPE",
      "HOME",
      "END",
      "PAGEUP",
      "PAGEDOWN",
    ];

    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener("keydown", (event) => {
      if (navKeys.includes((event.code || "").toUpperCase())) {
        mouseClick = false;
      }
    });

    window.addEventListener("mousedown", () => {
      mouseClick = true;
    });

    window.addEventListener(
      "focus",
      () => {
        if (currentFocusedElement) currentFocusedElement.classList?.remove("focused");
        if (mouseClick) return;

        currentFocusedElement = document.activeElement;
        currentFocusedElement?.classList?.add("focused");
      },
      true
    );
  } // ✅ CRITICAL: close the polyfill here (DO NOT let Dawn core fall inside it)

  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector("input");
      if (!this.input) return;

      this.changeEvent = new Event("change", { bubbles: true });
      this.input.addEventListener("change", this.onInputChange.bind(this));

      this.querySelectorAll("button").forEach((button) =>
        button.addEventListener("click", this.onButtonClick.bind(this))
      );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback() {
      if (!this.input) return;
      this.validateQtyRules();

      // SAFETY: subscribe/PUB_SUB_EVENTS may not exist if file is partial/duplicated
      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined" &&
        PUB_SUB_EVENTS.quantityUpdate
      ) {
        this.quantityUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.quantityUpdate,
          this.validateQtyRules.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
    }

    onInputChange() {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      if (!this.input) return;
      event.preventDefault();
      const previousValue = this.input.value;

      event.target?.name === "plus" ? this.input.stepUp() : this.input.stepDown();
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    validateQtyRules() {
      if (!this.input) return;

      const value = parseInt(this.input.value, 10);

      if (this.input.min) {
        const min = parseInt(this.input.min, 10);
        const buttonMinus = this.querySelector(".quantity__button[name='minus']");
        if (buttonMinus) buttonMinus.classList.toggle("disabled", value <= min);
      }

      if (this.input.max) {
        const max = parseInt(this.input.max, 10);
        const buttonPlus = this.querySelector(".quantity__button[name='plus']");
        if (buttonPlus) buttonPlus.classList.toggle("disabled", value >= max);
      }
    }
  }

  // SAFETY: prevent "already defined" errors if script is duplicated by apps/edits
  if (!customElements.get("quantity-input")) {
    customElements.define("quantity-input", QuantityInput);
  }

  /* Fix debounce/throttle to preserve caller 'this' correctly */
  function debounce(fn, wait) {
    let t;
    return function (...args) {
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), wait);
    };
  }
  // Make debounce available to Dawn core scripts (facets, search, predictive search)
  window.debounce = debounce;

  function throttle(fn, delay) {
    let lastCall = 0;
    return function (...args) {
      const ctx = this;
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      return fn.apply(ctx, args);
    };
  }

  function fetchConfig(type = "json") {
    return {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: `application/${type}` },
    };
  }

  /*
   * Shopify Common JS
   */
  if (typeof window.Shopify === "undefined") {
    window.Shopify = {};
  }

  Shopify.bind = function (fn, scope) {
    return function () {
      return fn.apply(scope, arguments);
    };
  };

  Shopify.setSelectorByValue = function (selector, value) {
    for (var i = 0, count = selector?.options?.length || 0; i < count; i++) {
      var option = selector.options[i];
      if (value == option.value || value == option.innerHTML) {
        selector.selectedIndex = i;
        return i;
      }
    }
  };

  Shopify.addListener = function (target, eventName, callback) {
    if (!target) return;
    target.addEventListener
      ? target.addEventListener(eventName, callback, false)
      : target.attachEvent("on" + eventName, callback);
  };

  Shopify.postLink = function (path, options) {
    options = options || {};
    var method = options["method"] || "post";
    var params = options["parameters"] || {};

    var form = document.createElement("form");
    form.setAttribute("method", method);
    form.setAttribute("action", path);

    for (var key in params) {
      var hiddenField = document.createElement("input");
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("name", key);
      hiddenField.setAttribute("value", params[key]);
      form.appendChild(hiddenField);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
    this.countryEl = document.getElementById(country_domid);
    this.provinceEl = document.getElementById(province_domid);
    this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

    Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

    this.initCountry();
    this.initProvince();
  };

  Shopify.CountryProvinceSelector.prototype = {
    initCountry: function () {
      if (!this.countryEl) return;
      var value = this.countryEl.getAttribute("data-default");
      Shopify.setSelectorByValue(this.countryEl, value);
      this.countryHandler();
    },

    initProvince: function () {
      if (!this.provinceEl) return;
      var value = this.provinceEl.getAttribute("data-default");
      if (value && this.provinceEl.options.length > 0) {
        Shopify.setSelectorByValue(this.provinceEl, value);
      }
    },

    countryHandler: function () {
      if (!this.countryEl || !this.provinceEl || !this.provinceContainer) return;

      var opt = this.countryEl.options[this.countryEl.selectedIndex];
      var raw = opt?.getAttribute?.("data-provinces");

      var provinces = [];
      try {
        provinces = raw ? JSON.parse(raw) : [];
      } catch (e) {
        provinces = [];
      }

      this.clearOptions(this.provinceEl);
      if (!provinces || provinces.length == 0) {
        this.provinceContainer.style.display = "none";
      } else {
        for (var i = 0; i < provinces.length; i++) {
          var opt = document.createElement("option");
          opt.value = provinces[i][0];
          opt.innerHTML = provinces[i][1];
          this.provinceEl.appendChild(opt);
        }
        this.provinceContainer.style.display = "";
      }
    },

    clearOptions: function (selector) {
      if (!selector) return;
      while (selector.firstChild) selector.removeChild(selector.firstChild);
    },

    setOptions: function (selector, values) {
      if (!selector) return;
      for (var i = 0; i < values.length; i++) {
        var opt = document.createElement("option");
        opt.value = values[i];
        opt.innerHTML = values[i];
        selector.appendChild(opt);
      }
    },
  };

  /* ========= Menu drawers (patched 990px matchMedia bug) ========= */

  class MenuDrawer extends HTMLElement {
    constructor() {
      super();

      this.mainDetailsToggle = this.querySelector("details");
      if (!this.mainDetailsToggle) return;

      this.addEventListener("keyup", this.onKeyUp.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.bindEvents();
    }

    bindEvents() {
      this.querySelectorAll("summary").forEach((summary) =>
        summary.addEventListener("click", this.onSummaryClick.bind(this))
      );
      this.querySelectorAll("button:not(.localization-selector)").forEach((button) =>
        button.addEventListener("click", this.onCloseButtonClick.bind(this))
      );
    }

    onKeyUp(event) {
      if ((event.code || "").toUpperCase() !== "ESCAPE") return;

      const openDetailsElement = event.target.closest("details[open]");
      if (!openDetailsElement) return;

      openDetailsElement === this.mainDetailsToggle
        ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
        : this.closeSubmenu(openDetailsElement);
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest(".has-submenu");
      const isOpen = detailsElement.hasAttribute("open");
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      const addTrapFocus = () => {
        trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
        summaryElement.nextElementSibling?.removeEventListener?.("transitionend", addTrapFocus);
      };

      if (detailsElement === this.mainDetailsToggle) {
        if (isOpen) event.preventDefault();
        isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

        if (window.matchMedia("(max-width: 990px)").matches) {
          document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
        }
      } else {
        setTimeout(() => {
          detailsElement.classList.add("menu-opening");
          summaryElement.setAttribute("aria-expanded", true);
          parentMenuElement && parentMenuElement.classList.add("submenu-open");

          reducedMotion.matches
            ? addTrapFocus()
            : summaryElement.nextElementSibling?.addEventListener?.("transitionend", addTrapFocus);
        }, 100);
      }
    }

    openMenuDrawer(summaryElement) {
      setTimeout(() => {
        this.mainDetailsToggle.classList.add("menu-opening");
      });
      summaryElement.setAttribute("aria-expanded", true);
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus = false) {
      if (event === undefined || !this.mainDetailsToggle) return;

      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);

      if (event instanceof KeyboardEvent && elementToFocus?.setAttribute)
        elementToFocus.setAttribute("aria-expanded", false);
    }

    onFocusOut() {
      setTimeout(() => {
        if (this.mainDetailsToggle?.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) {
          this.closeMenuDrawer();
        }
      });
    }

    onCloseButtonClick(event) {
      const detailsElement = event.currentTarget.closest("details");
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      if (!detailsElement) return;
      const parentMenuElement = detailsElement.closest(".submenu-open");
      parentMenuElement && parentMenuElement.classList.remove("submenu-open");
      detailsElement.classList.remove("menu-opening");
      detailsElement.querySelector("summary")?.setAttribute("aria-expanded", false);
      removeTrapFocus(detailsElement.querySelector("summary"));
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      if (!detailsElement) return;
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) animationStart = time;
        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute("open");
          const openParent = detailsElement.closest("details[open]");
          if (openParent) trapFocus(openParent, detailsElement.querySelector("summary"));
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }

  if (!customElements.get("menu-drawer")) {
    customElements.define("menu-drawer", MenuDrawer);
  }

  class HeaderDrawer extends MenuDrawer {
    constructor() {
      super();
    }

    openMenuDrawer(summaryElement) {
      this.header = this.header || document.querySelector(".section-header");

      const hasBorderBottom =
        this.closest(".header-wrapper")?.classList?.contains("header-wrapper--border-bottom") || false;
      this.borderOffset = this.borderOffset !== undefined ? this.borderOffset : hasBorderBottom ? 1 : 0;

      if (this.header) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
        );
        this.header.classList.add("menu-open");
      }

      setTimeout(() => {
        this.mainDetailsToggle?.classList?.add("menu-opening");
      });

      summaryElement.setAttribute("aria-expanded", true);
      window.addEventListener("resize", this.onResize);
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus) {
      if (!elementToFocus) return;
      super.closeMenuDrawer(event, elementToFocus);
      this.header?.classList?.remove("menu-open");
      window.removeEventListener("resize", this.onResize);
    }

    onResize = () => {
      if (this.header) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
        );
      }
      document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
    };
  }

  if (!customElements.get("header-drawer")) {
    customElements.define("header-drawer", HeaderDrawer);
  }

  /* ========= Modal + Deferred Media ========= */

  class ModalDialog extends HTMLElement {
    constructor() {
      super();

      const closeBtn = this.querySelector('[id^="ModalClose-"]');
      if (closeBtn) closeBtn.addEventListener("click", this.hide.bind(this, false));

      this.addEventListener("keyup", (event) => {
        if ((event.code || "").toUpperCase() === "ESCAPE") this.hide();
      });

      if (this.classList.contains("media-modal")) {
        this.addEventListener("pointerup", (event) => {
          if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
        });
      } else {
        this.addEventListener("click", (event) => {
          if (event.target === this) this.hide();
        });
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      const popup = this.querySelector(".template-popup");
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      if (popup) popup.loadContent();
      trapFocus(this, this.querySelector('[role="dialog"]'));
      pauseAllMedia();
    }

    hide() {
      document.body.classList.remove("overflow-hidden");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
      this.removeAttribute("open");
      removeTrapFocus(this.openedBy);
      pauseAllMedia();
    }
  }

  if (!customElements.get("modal-dialog")) {
    customElements.define("modal-dialog", ModalDialog);
  }

  // --- TT FIX: expose ModalDialog globally for scripts that reference it ---
  window.ModalDialog = ModalDialog;

  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector("button");
      if (!button) return;

      button.addEventListener("click", () => {
        const modal = document.querySelector(this.getAttribute("data-modal"));
        if (modal && typeof modal.show === "function") modal.show(button);
      });
    }
  }

  if (!customElements.get("modal-opener")) {
    customElements.define("modal-opener", ModalOpener);
  }

  class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener("click", this.loadContent.bind(this));
    }

    loadContent(focus = true) {
      pauseAllMedia();
      if (!this.getAttribute("loaded")) {
        const tpl = this.querySelector("template");
        if (!tpl?.content?.firstElementChild) return;

        const content = document.createElement("div");
        content.appendChild(tpl.content.firstElementChild.cloneNode(true));

        this.setAttribute("loaded", true);
        const deferredElement = content.querySelector("video, model-viewer, iframe");
        if (!deferredElement) return;

        this.appendChild(deferredElement);
        if (focus && deferredElement.focus) deferredElement.focus();

        if (deferredElement.nodeName === "VIDEO" && deferredElement.getAttribute("autoplay")) {
          deferredElement.play?.();
        }
      }
    }
  }

  if (!customElements.get("deferred-media")) {
    customElements.define("deferred-media", DeferredMedia);
  }

  /* =========================================================
     TT: Placement Guides + Product Buttons — UNIFIED (single bind)
     ========================================================= */

  (function TTPlacementAndProductActions() {
    if (window.__TT_PLACEMENT_AND_PRODUCT_BOUND__) return;
    window.__TT_PLACEMENT_AND_PRODUCT_BOUND__ = true;

    function getOverlayId(type) {
      const t = String(type || "").trim();
      if (!t) return "";
      const map = {
        tees: "Tees",
        polos: "Polos",
        hats: "Hats",
        sweats: "Sweats",
        jackets: "Jackets",
        workshirts: "Workshirts",
        pants: "Pants",
      };
      const suffix = map[t] || (t.charAt(0).toUpperCase() + t.slice(1));
      return `ttPlacementOverlay${suffix}`;
    }

    function getOverlayElByType(type) {
      const id = getOverlayId(type);
      return id ? document.getElementById(id) : null;
    }

    function typeFromOverlayEl(overlay) {
      if (!overlay || !overlay.id) return "";
      const m = String(overlay.id).match(/^ttPlacementOverlay(.+)$/);
      if (!m) return "";
      const suffix = m[1];
      return suffix ? suffix.charAt(0).toLowerCase() + suffix.slice(1) : "";
    }

    function openGuide(type) {
      const overlay = getOverlayElByType(type);
      if (!overlay) return;
      overlay.removeAttribute("hidden");
      overlay.setAttribute("aria-hidden", "false");
      overlay.setAttribute("data-tt-open", "true");
    }

    function closeGuide(type) {
      const overlay = getOverlayElByType(type);
      if (!overlay) return;
      overlay.setAttribute("aria-hidden", "true");
      overlay.setAttribute("data-tt-open", "false");
      overlay.setAttribute("hidden", "");
    }

    function closeAllGuides() {
      document.querySelectorAll('.tt-modal-overlay[aria-hidden="false"]').forEach((overlay) => {
        overlay.setAttribute("aria-hidden", "true");
        overlay.setAttribute("data-tt-open", "false");
        overlay.setAttribute("hidden", "");
      });
    }

    // Public API (compat)
    window.TToppenPlacementGuide =
      window.TToppenPlacementGuide ||
      function (type) {
        openGuide(type);
      };
    window.TTclosePlacementGuide =
      window.TTclosePlacementGuide ||
      function (type) {
        closeGuide(type);
      };

    function guideTypeFromTags(tagsStr) {
      let tags = [];
      try {
        const parsed = JSON.parse(tagsStr);
        if (Array.isArray(parsed)) tags = parsed.map(String);
      } catch (e) {
        tags = String(tagsStr || "")
          .split("|")
          .map((t) => t.trim())
          .filter(Boolean);
      }

      const catTag = tags
        .map((t) => String(t).toLowerCase())
        .find((t) => t.startsWith("cat-"));
      if (!catTag) return "";

      const cat = catTag.replace("cat-", "").trim();

      // INTERNAL overlay types (keep tees here!)
      if (cat === "tshirts" || cat === "t-shirts" || cat === "shirts") return "tees";
      if (cat === "polos") return "polos";
      if (cat === "sweats" || cat === "hoodies" || cat === "sweatshirts") return "sweats";
      if (cat === "hats" || cat === "beanies") return "hats";
      if (cat === "jackets" || cat === "outerwear") return "jackets";
      if (cat === "workshirts") return "workshirts";
      if (cat === "pants") return "pants";
      return "";
    }

    // ---- Guide click handling (capture) ----
    document.addEventListener(
      "click",
      function (e) {
        const guideBtn = e.target.closest("[data-tt-guide]");
        if (guideBtn) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();

          const type = (guideBtn.getAttribute("data-tt-guide") || "").trim();
          if (type && typeof window.TToppenPlacementGuide === "function") {
            window.TToppenPlacementGuide(type);
          }
          return;
        }

        const legacyGuideBtn = e.target.closest("[data-tt-guide-btn]");
        if (legacyGuideBtn) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();

          const type = (
            legacyGuideBtn.getAttribute("data-tt-guide-btn") ||
            legacyGuideBtn.dataset.ttGuideType ||
            ""
          ).trim();

          if (type && typeof window.TToppenPlacementGuide === "function") {
            window.TToppenPlacementGuide(type);
          }
          return;
        }

        const openAttrBtn = e.target.closest("[data-tt-guide-open]");
        if (openAttrBtn) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();

          let type = (openAttrBtn.getAttribute("data-tt-guide-open") || "").trim();

          if (!type) {
            const bar = openAttrBtn.closest(".tt-actionbar--product[data-tt-product-tags]");
            const tagsStr = bar ? bar.getAttribute("data-tt-product-tags") || "" : "";
            type = guideTypeFromTags(tagsStr) || "";
            if (type) openAttrBtn.setAttribute("data-tt-guide-open", type);
          }

          if (type && typeof window.TToppenPlacementGuide === "function") {
            window.TToppenPlacementGuide(type);
          }
          return;
        }

        const closeBtn = e.target.closest("[data-tt-close]");
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();

          const explicit = (closeBtn.getAttribute("data-tt-close") || "").trim();
          if (explicit) closeGuide(explicit);
          else {
            const overlay = closeBtn.closest(".tt-modal-overlay");
            const inferred = typeFromOverlayEl(overlay);
            if (inferred) closeGuide(inferred);
            else closeAllGuides();
          }
          return;
        }

        const backdrop = e.target.closest("[data-tt-backdrop]");
        if (backdrop && backdrop === e.target) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();

          const explicit = (backdrop.getAttribute("data-tt-backdrop") || "").trim();
          if (explicit) closeGuide(explicit);
          else {
            const overlay = backdrop.closest(".tt-modal-overlay");
            const inferred = typeFromOverlayEl(overlay);
            if (inferred) closeGuide(inferred);
            else closeAllGuides();
          }
          return;
        }
      },
      true
    );

    document.addEventListener("keydown", (e) => {
      if ((e.key || "").toLowerCase() !== "escape") return;
      if (!document.querySelector('.tt-modal-overlay[aria-hidden="false"]')) return;
      closeAllGuides();
    });
  })();

  (function () {
    function initTTStickyBar() {
      const bar = document.querySelector("[data-tt-sticky]");
      if (!bar) return;

      const homeBtn = bar.querySelector(".tt-btn--orange");
      const href = homeBtn ? homeBtn.getAttribute("href") || "" : "";
      const match = href.match(/^\/collections\/([^/?#]+)/);
      const clientSlug = match ? match[1] : "";

      if (clientSlug && window.location.pathname === `/collections/${clientSlug}`) {
        bar.style.display = "none";
        return;
      }

      bar.style.display = "";
    }

    function safeInitProductActionbars() {
      if (typeof window.initProductActionbars === "function") {
        window.initProductActionbars();
      }
    }

    document.addEventListener("DOMContentLoaded", () => {
      safeInitProductActionbars();
      initTTStickyBar();
    });

    window.addEventListener("pageshow", () => {
      safeInitProductActionbars();
      initTTStickyBar();
    });

    safeInitProductActionbars();
    initTTStickyBar();
  })();

  // --- TT FIX: expose Dawn focus helpers globally (Dawn scripts expect these on window) ---
  try {
    if (typeof trapFocus === "function") window.trapFocus = trapFocus;
    if (typeof removeTrapFocus === "function") window.removeTrapFocus = removeTrapFocus;
  } catch (e) {
    console.warn("TT: Could not expose trapFocus/removeTrapFocus", e);
  }
})();
